import { useState, useCallback, useRef } from "react";
import "@/App.css";
import { Toaster } from "sonner";

import { HeroVideo } from "@/components/HeroVideo";
import { ClassFormatModal } from "@/components/ClassFormatModal";

import { DifferentiatorBand } from "@/components/sections/DifferentiatorBand";
import { MethodSection } from "@/components/sections/MethodSection";
import { ClassesSection } from "@/components/sections/ClassesSection";
import { FoundationSection } from "@/components/sections/FoundationSection";
import { StudioExperienceSection } from "@/components/sections/StudioExperienceSection";
import { EventsSection } from "@/components/sections/EventsSection";
import { TestimonialsSection } from "@/components/sections/TestimonialsSection";
import { PrivatesSection } from "@/components/sections/PrivatesSection";
import { ContactSection } from "@/components/sections/ContactSection";
import { Footer } from "@/components/sections/Footer";

import {
  FALLBACK_CLASS_FORMATS,
  FALLBACK_TESTIMONIALS,
} from "@/data/content";

function App({ onNavigate }) {
  const [activeFormat, setActiveFormat] = useState(null);
  const [formatOpen, setFormatOpen] = useState(false);
  const [presetInterest, setPresetInterest] = useState({
    value: "",
    sequence: 0,
  });
  const formatTriggerRef = useRef(null);

  // Content is bundled directly into the build (no backend needed for display),
  // which keeps the site fully self-contained for static hosting (e.g. Netlify).
  const formats = FALLBACK_CLASS_FORMATS;
  const testimonials = FALLBACK_TESTIMONIALS;

  const openLiveSchedule = useCallback(() => {
    onNavigate("/digital-studio#live");
  }, [onNavigate]);

  const openFormat = useCallback((f, trigger) => {
    if (trigger?.focus) {
      formatTriggerRef.current = trigger;
    }
    setActiveFormat(f);
    setFormatOpen(true);
  }, []);

  const restoreFormatFocus = useCallback((event) => {
    const trigger = formatTriggerRef.current;
    if (trigger && document.contains(trigger)) {
      event.preventDefault();
      trigger.focus();
    }
  }, []);

  const goInquire = useCallback((interest) => {
    if (typeof interest === "string") {
      setPresetInterest((current) => ({
        value: interest,
        sequence: current.sequence + 1,
      }));
    }
    const el = document.getElementById("contact");
    if (el) {
      const y = el.getBoundingClientRect().top + window.scrollY - 96;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  }, []);

  return (
    <div className="App">
      <Toaster
        position="bottom-right"
        toastOptions={{
          style: {
            background: "var(--surface-3)",
            color: "var(--text)",
            border: "1px solid var(--border-c)",
          },
        }}
      />

      <main>
        <HeroVideo
          onInquire={() => goInquire("Private Training")}
        />
        <DifferentiatorBand />
        <MethodSection />
        <StudioExperienceSection />
        <ClassesSection formats={formats} onOpenFormat={openFormat} />
        <FoundationSection />
        <PrivatesSection
          onInquire={() => goInquire("Private Training")}
        />
        <EventsSection onInquire={() => goInquire("Events & Partnerships")} />
        <TestimonialsSection testimonials={testimonials} />
        <ContactSection presetInterest={presetInterest} />
      </main>

      <Footer />

      <ClassFormatModal
        format={activeFormat}
        open={formatOpen}
        onOpenChange={setFormatOpen}
        onCloseAutoFocus={restoreFormatFocus}
        onViewLive={() => {
          setFormatOpen(false);
          setTimeout(openLiveSchedule, 180);
        }}
      />
    </div>
  );
}

export default App;
